import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {composeActions} from 'ducks/common';
import {
  fetchCreditNotes,
  selectCreditNotes,
  fetchScheduleTypesFilters,
  selectScheduleTypesFilters,
  selectCreditNotesLoading
} from 'ducks/payments';
import {exportCsv} from 'ducks/files';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {exportCreditNotesCsv as exportCreditNotesCsvSdk} from 'sdk/payments';

import Page from 'components/Page';
import Loading from 'components/Loading';
import VisibleIf from 'components/VisibleIf';
import ListContent from 'components/ListContent';

import {Container} from 'containers/types';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';

import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';

import {
  CreditNotesListTable,
  SubNavigation,
  CreditNotesListFilters
} from './components';

import {getUpdatedFilter} from 'utils/filters';

import './styles.css';

const PAYMENTS_CSV_FILENAME = 'credit-notes';

const initialFilters: {
  search: string;
  scheduleTypes: string[];
  statuses: string[];
  xeroStatuses: string[];
  customer: string | null;
  dateIssuedStart: string | null;
  dateIssuedEnd: string | null;
  createdAtStart: string | null;
  createdAtEnd: string | null;
  createdBy: string | null;
  createdByTeam: number | null;
  rentStartDateStart: string | null;
  rentStartDateEnd: string | null;
  rentEndDateStart: string | null;
  rentEndDateEnd: string | null;
} = {
  search: '',
  scheduleTypes: [],
  statuses: [],
  xeroStatuses: [],
  customer: null,
  dateIssuedStart: null,
  dateIssuedEnd: null,
  createdAtStart: null,
  createdAtEnd: null,
  createdBy: null,
  createdByTeam: null,
  rentStartDateStart: null,
  rentStartDateEnd: null,
  rentEndDateStart: null,
  rentEndDateEnd: null
};

class CreditNotesList extends React.Component<
  Container &
    LoginRequiredProps &
    WithPaginationProps &
    WithFiltersProps<typeof initialFilters> &
    WithSortingProps &
    ReturnType<typeof mapsStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CREDIT_NOTES_LIST,
        locationRef
      },
      () => {
        this.props.fetchCreditNotes({locationRef});
        this.props.fetchScheduleTypesFilters({locationRef});
      }
    );
  }

  onPageChange = ({offset, pageSize}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({offset, pageSize}, [
      () => fetchCreditNotes({locationRef})
    ]);
  };

  filterCreditNotesBy = (field, value) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    const updatedFilter = getUpdatedFilter(
      {field, value},
      this.props.getFilters()
    );

    this.props.composeActions([
      () => this.props.setFilter(updatedFilter.field, updatedFilter.value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchCreditNotes({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchCreditNotes({locationRef})
    ]);
  };

  onSort = fieldName => {
    const {locationRef} = this.props.match.params;
    const pagination = this.props.getPagination();

    this.props.onSort(fieldName, [
      () => fetchCreditNotes({locationRef}),
      () =>
        this.props.onPaginationChange({
          pageSize: pagination.pageSize,
          offset: 0
        })
    ]);
  };

  redirectToScheduleItem = ({link}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        window.open(link);
      }
    );
  };

  exportCreditNotesCsv = () => {
    const {locationRef} = this.props.match.params;
    const filters = this.props.getFilters();
    const sort = this.props.getSort();

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_CREDIT_NOTES_LIST, locationRef},
      () => {
        this.props.exportCsv({
          exportData: {
            locationRef,
            filters,
            sort
          },
          exportSdk: exportCreditNotesCsvSdk,
          fileName: PAYMENTS_CSV_FILENAME
        });
      }
    );
  };

  render() {
    const {
      match: {params: {locationRef}},
      creditNotes,
      scheduleTypesFilters,
      areCreditNotesLoading
    } = this.props;

    return (
      <Page activeTab="creditNotes" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_CREDIT_NOTES_LIST}
          locationRef={locationRef}>
          <ListContent
            className="CreditNotesList"
            initialFilters={initialFilters}
            filters={this.props.getFilters()}
            setFilter={this.filterCreditNotesBy}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {areCreditNotesLoading && <Loading />}
              {!areCreditNotesLoading && (
                <CreditNotesListTable
                  creditNotes={creditNotes}
                  pagination={this.props.getPagination()}
                  onPageChange={this.onPageChange}
                  sort={this.props.getSort()}
                  onSort={this.onSort}
                  redirectToScheduleItem={this.redirectToScheduleItem}
                  exportCreditNotesCsv={this.exportCreditNotesCsv}
                />
              )}
            </ListContent.Content>
            <ListContent.Filters>
              <CreditNotesListFilters
                locationRef={locationRef}
                getFilters={this.props.getFilters}
                setFilter={this.filterCreditNotesBy}
                clearFilters={this.clearFilters}
                scheduleTypesFilters={scheduleTypesFilters}
              />
            </ListContent.Filters>
          </ListContent>
        </VisibleIf>
      </Page>
    );
  }
}

const mapsStateToProps = state => {
  return {
    creditNotes: selectCreditNotes(state),
    areCreditNotesLoading: selectCreditNotesLoading(state),
    scheduleTypesFilters: selectScheduleTypesFilters(state)
  };
};

const mapDispatchToProps = {
  composeActions,
  requiresPermission,
  fetchCreditNotes,
  fetchScheduleTypesFilters,
  exportCsv
};

const authenticatedComponent = LoginRequired(CreditNotesList);

const componentWithPagination = withPagination(
  authenticatedComponent,
  PAGINATION.CREDIT_NOTES_LIST,
  {
    saveToUrl: true
  }
);

const componentWithPaginationAndFilters = withFilters(
  componentWithPagination,
  FILTERS.CREDIT_NOTES_LIST,
  {initialFilters, saveToUrl: true}
);

const componentWithPaginationAndFiltersAndSorting = withSorting(
  componentWithPaginationAndFilters,
  SORTING.CREDIT_NOTES_LIST,
  {saveToUrl: true}
);

export default connect(mapsStateToProps, mapDispatchToProps)(
  componentWithPaginationAndFiltersAndSorting
);
